import { get } from 'lodash-es'
import { parsePrimaryKeyAndSource, compareValuesNullsLast } from '@/components/shared-table/utils'
import { MODELS_API } from '@/main'
import {
  buildColumnDef,
  getFormattedValue,
  getModelPredictionRoot,
  isPredictedField,
  formatValueWithConversion,
  getChemicalFromExtras,
} from '@/components/shared-table/buildColumnDef'
import { getChemicalMassFromParams } from '@/components/shared-table/utils'
import { getStandardizationChange } from '@/utils/chemical'

const getLink = (modelId, chemical) => {
  if (!modelId || !chemical) return null
  if (!chemical.smiles && !chemical.canonicalSmiles) return null
  return `${MODELS_API}/predictor_models/predict?model_id=${modelId}&report_format=html&smiles=${encodeURIComponent(chemical.smiles || chemical.canonicalSmiles)}`
}

const findPredictionContextByFieldKey = ({ modelId, predictionsByModelId, fieldKey, extrasByModelId, chemical }) => {
  const predictionForModel = getModelPredictionRoot(predictionsByModelId, modelId)
  if (!predictionForModel) return null

  const { sourceType } = parsePrimaryKeyAndSource(fieldKey, modelId)

  const { standardizedChemical, sourceChemical } = getChemicalFromExtras(extrasByModelId, modelId)

  return {
    sourceType,
    predictionForModel,
    link: sourceType === 'predicted' && predictionForModel ? getLink(modelId, sourceChemical) : null,
    standardizationChange: getStandardizationChange(standardizedChemical, chemical),
  }
}

const getPredictionRawValue = (predictionForModel, sourceType) => {
  const valueKey =
    sourceType === 'experimental'
      ? 'experimentalValueUnitsDisplay'
      : sourceType === 'predicted'
        ? 'predictionValueUnitsDisplay'
        : null

  return get(predictionForModel, valueKey, null)
}

const buildToddsModelsColumnDef = ({ column, visibleColumnIds, tableSettings }) => {
  return buildColumnDef({
    column,
    visibleColumnIds,
    tableSettings,

    ...(isPredictedField(column?.id)
      ? {
          tooltipComponent: 'ToddsModelsTooltip',

          tooltipComponentParams: (params) => {
            const predictionContext = findPredictionContextByFieldKey({
              modelId: column?.modelId,
              predictionsByModelId: params?.data?.predictionsByModelId,
              extrasByModelId: params?.data?.extrasByModelId,
              fieldKey: params?.colDef?.field,
              chemical: params?.data?.chemical,
            })
            return predictionContext ? { ...predictionContext } : null
          },

          cellClassRules: {
            'text-warning': (params) =>
              params?.value?.sourceType === 'predicted' &&
              params?.value?.predictionForModel?.adEstimates?.[0]?.AD === false,

            'replaced-chemical-cell': (params) => {
              const rawValue = getPredictionRawValue(params?.value?.predictionForModel, params?.value?.sourceType)
              return !!rawValue && !!params?.value?.standardizationChange
            },
          },
        }
      : {}),

    ...(isPredictedField(column?.id)
      ? {
          onCellClicked: (params) => {
            const link = params?.value?.link
            if (!link) return

            window.open(link, '_blank', 'noopener,noreferrer')
          },
        }
      : {}),

    valueGetter: (params) =>
      findPredictionContextByFieldKey({
        modelId: column?.modelId,
        predictionsByModelId: params?.data?.predictionsByModelId,
        fieldKey: params?.colDef?.field,
        extrasByModelId: params?.data?.extrasByModelId,
        chemical: params?.data?.chemical,
      }),

    valueFormatter: (params) =>
      getFormattedValue({
        params,
        column,
        formatFunction: () => {
          const rawValue = getPredictionRawValue(params?.value?.predictionForModel, params?.value?.sourceType)
          return formatValueWithConversion({
            value: rawValue,
            fromUnit: column?.originalUnit,
            toUnit: column?.unit,
            mass: getChemicalMassFromParams(params),
          })
        },
      }),

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) => {
      const rawAValue = getPredictionRawValue(aValue?.predictionForModel, aValue?.sourceType)
      const rawBValue = getPredictionRawValue(bValue?.predictionForModel, bValue?.sourceType)
      return compareValuesNullsLast(rawAValue, rawBValue, isDescending)
    },
  })
}

export { buildToddsModelsColumnDef }
